import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import type { PaytrieAPI } from '@paytrie/client'

import { getDailyAvailability } from '../maintenanceTimeline'
import type { MaintenanceFormatters } from '../useMaintenanceFormatters'
import { AvailabilityBars } from './AvailabilityBars'
import { HISTORY_RANGES } from './historyRanges'
import { CARD_CLASS_NAME, MUTED_TEXT_CLASS_NAME } from './styles'
import { WindowCountdown } from './WindowCountdown'

type StatusCardProps = {
  windows: PaytrieAPI.MaintenanceWindow[]
  currentWindow: PaytrieAPI.MaintenanceWindow | undefined
  now: Date
  formatters: MaintenanceFormatters
}

export const StatusCard = memo(function StatusCard({
  windows,
  currentWindow,
  now,
  formatters,
}: StatusCardProps) {
  const { t } = useTranslation()
  const days = useMemo(
    () => getDailyAvailability({ windows, now }),
    [windows, now]
  )
  const isUnderMaintenance = currentWindow !== undefined

  return (
    <section
      data-testid="maintenance-status-card"
      className={clsx(CARD_CLASS_NAME, 'px-5 py-4')}
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gravel dark:text-snow">
            {t('maintenance.status.transactions')}
          </h2>
          <p
            aria-live="polite"
            className={clsx(
              'mt-1 flex items-center gap-2 text-sm font-medium',
              isUnderMaintenance
                ? 'text-amber-700 dark:text-amber-300'
                : 'text-emerald-700 dark:text-emerald-300'
            )}
          >
            <span
              className={clsx(
                'h-2.5 w-2.5 rounded-full',
                isUnderMaintenance ? 'bg-amber-400' : 'bg-emerald-500'
              )}
            />
            {isUnderMaintenance
              ? t('maintenance.status.inProgress')
              : t('maintenance.status.operational')}
          </p>
        </div>
        {currentWindow?.endsAt && (
          <WindowCountdown endsAt={currentWindow.endsAt} />
        )}
      </div>
      <AvailabilityBars days={days} formatters={formatters} />
      <div
        className={clsx(
          'mt-2 flex items-center gap-2 whitespace-nowrap text-xs',
          MUTED_TEXT_CLASS_NAME
        )}
      >
        {HISTORY_RANGES.map((range) => (
          <span key={range.days} className={range.labelClassName}>
            {t('maintenance.bars.daysAgo', { count: range.days })}
          </span>
        ))}
        <span className="h-px flex-1 bg-cloudDarker dark:bg-darkCharcoal" />
        <span>{t('maintenance.bars.today')}</span>
      </div>
    </section>
  )
})
